/**
 * What the plan allows, rendered honestly.
 *
 * Requirement 4 of the slice, and the honesty is the requirement. Three things this screen
 * is careful about:
 *
 *   1. It shows what `GET /v1/me/entitlements` **actually returned**, including the codes
 *      the shell has no screen for. A list filtered down to "things this UI happens to
 *      render" would quietly misreport the plan.
 *   2. It says, on the screen and not only in a comment, that this list decides what is
 *      worth showing and never what is allowed. Invariant 7: the server re-runs
 *      `require_feature` on every gated route and it is the only thing standing between a
 *      caller and a capability.
 *   3. A suspended tenant is told it is suspended. An empty feature set rendered as a
 *      module with every button greyed out and no explanation is the failure mode the
 *      entitlements schema's own docstring warns about — the kill switch is a billing
 *      state, not a bug, and the person looking at it can act on it if they know.
 */

import { Alert, Box, Chip, Stack, Typography } from '@mui/material';
import { FEATURES } from '@inai/inventory-core';
import { useEntitlements, useOrganization } from '@inai/inventory-core/react';
import { INVENTORY_ROUTES, ScreenHeader, SectionCard } from '@inai/inventory-mui';

/** Feature code → the screens in this shell that need it. Rendering only. */
const SCREENS_BY_FEATURE = new Map<string, string[]>();
for (const route of INVENTORY_ROUTES) {
  if (!route.feature) continue;
  const existing = SCREENS_BY_FEATURE.get(route.feature) ?? [];
  existing.push(route.label);
  SCREENS_BY_FEATURE.set(route.feature, existing);
}

const ALL_FEATURES: readonly string[] = Object.values(FEATURES);

export function PlanScreen() {
  const { data: entitlements, isLoading } = useEntitlements();
  const { data: organization } = useOrganization();

  const granted = new Set(entitlements?.features ?? []);
  // Codes the server granted that this client's catalogue does not know about. A newer
  // service is not an error; showing them as "missing" would be a lie.
  const unknown = (entitlements?.features ?? []).filter((code) => !ALL_FEATURES.includes(code));

  return (
    <Box>
      <ScreenHeader
        title="Plan"
        subtitle="What this organization's plan allows. The service re-checks every gated route on every request — this list decides what is worth showing, never what is permitted."
      />

      {entitlements?.suspended ? (
        <Alert severity="warning" variant="outlined" sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 13 }}>
            This organization is {organization?.status ?? 'suspended'}, so entitlements
            resolve to an empty set and every gated route refuses. Nothing is hidden
            selectively; billing is the switch.
          </Typography>
        </Alert>
      ) : null}

      <SectionCard
        title="Plan"
        hint={isLoading ? 'loading' : (entitlements?.plan ?? 'no plan on this organization')}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {ALL_FEATURES.map((code) => {
              const allowed = granted.has(code);
              const screens = SCREENS_BY_FEATURE.get(code);
              return (
                <Chip
                  key={code}
                  size="small"
                  label={
                    screens ? `${code.replace(/_/g, ' ')} — ${screens.join(', ')}` : code.replace(/_/g, ' ')
                  }
                  color={allowed ? 'success' : 'default'}
                  variant={allowed ? 'filled' : 'outlined'}
                  sx={{ opacity: allowed ? 1 : 0.55 }}
                />
              );
            })}
          </Stack>

          {unknown.length > 0 ? (
            <Typography color="text.secondary" sx={{ fontSize: 12.5, mt: 2 }}>
              The service also granted {unknown.join(', ')}, which this client version does
              not have a name for. That means the service is newer than this shell, not
              that something is wrong.
            </Typography>
          ) : null}
        </Box>
      </SectionCard>

      <Box sx={{ mt: 2 }}>
        <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
          A capability missing here is still reachable by URL, and the screen will load. The
          request behind it comes back <strong>403 feature_not_in_plan</strong>, naming the
          feature, from <code>core/api/deps.py::require_feature</code>. That is the
          enforcement; the navigation is a courtesy.
        </Typography>
      </Box>
    </Box>
  );
}
